import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  exportMeasureReport, measureReportPreflight, getMeasureTemplate,
  uploadMeasureTemplate, resetMeasureTemplate,
} from '../api/client';
import type { MeasureReportHeader, MeasureReportPreflight } from '../types';

/**
 * 「测量报告」导出弹窗（v0.32 需求 2）。
 *
 * 需求原话：「读取测量报告模板，将图纸信息、标准值、公差、测量工具等信息导入到测量报告模板里面」
 *
 * 用户已裁决的 5 条（2026-10-05），本组件按此实现：
 *  ① 表头 =「**4 字段**标题栏自动抽取 + 其余**弹窗补填**」，不启用大模型；
 *  ② 参考尺寸**纳入**，M 备注列标「(参考)」；
 *  ③ 特征数超模板容量时**自动插行**（弹窗如实预告插几行，不提示分批）；
 *  ④ E 列 CC/SC 本期不做；
 *  ⑤ 一次做完。
 *
 * 弹窗的信息层级刻意是「**先说清会导出什么 → 再让用户核对表头 → 最后才是按钮**」：
 * 表头是唯一需要人过目的东西（自动抽取只保证 4 个字段），而「会导出多少行、
 * 有多少条没公差、量具有几条没命中模板下拉」这些**风险**必须在点按钮之前看到 ——
 * 导完才发现，等于白导一遍。
 */

const LS_HEADER = 'gdt_balloon_measure_header';

/** 表头 12 字段的展示顺序（与模板 3–6 行的排布一致：先上排、再下排）。 */
const FIELDS: Array<{
  key: keyof MeasureReportHeader; label: string; cell: string;
  width?: 'sm' | 'md'; placeholder?: string;
}> = [
  { key: 'part_name', label: '零件名称', cell: 'D3' },
  { key: 'part_no', label: '零件编号', cell: 'I3' },
  { key: 'version', label: '图纸版本号', cell: 'M3', width: 'sm' },
  { key: 'inspector', label: '检测人', cell: 'D4', width: 'sm' },
  { key: 'material', label: '材质', cell: 'I4' },
  { key: 'drawing_no', label: '图纸编号', cell: 'M4' },
  { key: 'lot_no', label: '生产批号', cell: 'D5', width: 'sm' },
  { key: 'prod_qty', label: '生产数量', cell: 'I5', width: 'sm' },
  { key: 'ship_lot', label: '出货批号', cell: 'M5', width: 'sm' },
  { key: 'inspection_date', label: '检查日期', cell: 'D6', width: 'sm', placeholder: '2026-10-05' },
  { key: 'measure_qty', label: '测量数量', cell: 'I6', width: 'sm' },
  { key: 'ship_qty', label: '出货数量', cell: 'M6', width: 'sm' },
];

/* ⚠️ 「哪几个字段能自动抽」**不再在前端写死**（原来这里是 AUTO_FIELDS 常量）：
   它已经由后端 `header_source` 逐字段给出（auto / filename / empty），
   前端再留一份就是「同一语义两份实现」—— 后端加一个可锚定字段时，
   前端这份会静默漏掉，症状是"新字段明明抽到了却没有徽章"。 */

/**
 * **不参与「记住」的字段** —— 这 5 个是「本图身份」，逐图必变。
 *
 * 现场反馈（2026-10-08）「导出测量报告的表头每次都是这个编号」的根子就在这：
 * 标题栏抽不到时（本厂 13 张里 9 张如此），弹窗拿**上次记住的零件编号**补齐，
 * 于是每张图都写着同一张图的编号。
 *
 * 只给它打「上次」标记能让人看出问题，但**错值本身**还在输入框里，
 * 顺手点「导出」就进了报告 ⇒ 直接不填才是对的（宁可空，不可错）。
 * 检测人 / 检查日期 / 批号 / 数量这类**跨图复用**的字段照旧记忆，
 * 且一律打「上次」标记，不会伪装成本图抽到的值。
 */
const MEMO_SKIP: Array<keyof MeasureReportHeader> = [
  'part_no', 'drawing_no', 'part_name', 'version', 'material',
];

function loadRemembered(): MeasureReportHeader {
  try {
    const raw = localStorage.getItem(LS_HEADER);
    const o = raw ? JSON.parse(raw) : {};
    return (o && typeof o === 'object') ? o : {};
  } catch { return {}; }
}

interface Props {
  drawingId: number;
  pageIndex: number;
  onClose: () => void;
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

export function MeasureReportDialog({ drawingId, pageIndex, onClose, onToast }: Props) {
  const qc = useQueryClient();
  const [header, setHeader] = useState<MeasureReportHeader>({});
  const [remember, setRemember] = useState(true);
  // 哪些字段的值来自**上次导出的记忆**（localStorage）而不是本图 ——
  // 必须单独留痕：它长得像「抽取结果」，但其实是上一张图的编号。
  const [memoFilled, setMemoFilled] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const tplRef = useRef<HTMLInputElement>(null);

  const pfQ = useQuery({
    queryKey: ['measure-preflight', drawingId, pageIndex],
    queryFn: () => measureReportPreflight(drawingId, pageIndex),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // 自动抽取值 + 上次记住的值：**记住的值只补「自动抽不到」的字段**，
  // 且**绝不补本图身份字段**（见 `MEMO_SKIP`）。
  // 反过来（记住的值覆盖自动值）会让用户上张图手改的零件名黏到这张图上，
  // 那是比留空更危险的一类错误。
  // ⚠️ 补进去的值仍要记进 `memoFilled`：它长得像「抽取结果」，
  //    但其实是上一张图/上一次填的东西，必须打「上次」徽章。
  useEffect(() => {
    const pf = pfQ.data;
    if (!pf) return;
    const memo = loadRemembered();
    const next: MeasureReportHeader = {};
    const fromMemo = new Set<string>();
    for (const f of FIELDS) {
      const auto = (pf.header as any)?.[f.key] ?? '';
      if (auto) { next[f.key] = auto; continue; }
      const m = MEMO_SKIP.includes(f.key) ? '' : (memo as any)?.[f.key];
      if (m) { next[f.key] = m; fromMemo.add(f.key as string); }
      else next[f.key] = '';
    }
    setHeader(next);
    setMemoFilled(fromMemo);
    setTouched(new Set());
  }, [pfQ.data]);

  const pf: MeasureReportPreflight | undefined = pfQ.data;
  const tpl = pf?.template;

  const setField = (k: keyof MeasureReportHeader, v: string) => {
    setHeader((h) => ({ ...h, [k]: v }));
    setTouched((t) => new Set(t).add(k as string));
  };

  const srcOf = (k: keyof MeasureReportHeader): string => {
    if (touched.has(k as string)) return 'manual';
    const s = (pf?.header_source as any)?.[k];
    if (s) return s;
    if (memoFilled.has(k as string)) return 'last';
    return header[k] ? 'manual' : 'empty';
  };

  /** 文件名兜底候选（0/1/多）；多候选时后端**故意不填**，这里给点选。 */
  const fileCands = pf?.filename_candidates ?? [];

  const exportMut = useMutation({
    mutationFn: async () => {
      setBusy(true);
      if (remember) {
        try {
          // 只记**跨图复用**的字段：本图身份字段（MEMO_SKIP）既不读也不写 ——
          // 写进去也没意义（下次永远不会用它），而且会把上一张图的编号
          // 一直留在本机。整对象覆盖式写入 ⇒ 这次没写的键会被**移除**。
          localStorage.setItem(LS_HEADER, JSON.stringify(
            Object.fromEntries(Object.entries(header)
              .filter(([k, v]) => !MEMO_SKIP.includes(k as any)
                && (v ?? '').toString().trim()))));
        } catch { /* 忽略配额错误 */ }
      }
      // 附图页的气泡图按**当前画布页**取，避免多页图纸里放错页
      return exportMeasureReport(drawingId, header,
        { page: pageIndex, balloon: true });
    },
    onSuccess: (fn) => {
      setBusy(false);
      onToast(`已导出：${fn}`, 'success');
      qc.invalidateQueries({ queryKey: ['reports', drawingId] });
      onClose();
    },
    onError: (e: any) => {
      setBusy(false);
      onToast(`导出失败：${e.message}`, 'error');
    },
  });

  const tplMut = useMutation({
    mutationFn: async (f: File) => uploadMeasureTemplate(f),
    onSuccess: (info) => {
      onToast(`模板已替换：${info.filename}`, 'success');
      qc.invalidateQueries({ queryKey: ['measure-preflight'] });
      qc.invalidateQueries({ queryKey: ['measure-template'] });
    },
    onError: (e: any) => onToast(`模板不可用：${e.message}`, 'error'),
  });

  const resetMut = useMutation({
    mutationFn: () => resetMeasureTemplate(),
    onSuccess: () => {
      onToast('已恢复为内置模板', 'success');
      qc.invalidateQueries({ queryKey: ['measure-preflight'] });
    },
    onError: (e: any) => onToast(`恢复失败：${e.message}`, 'error'),
  });

  const tplQ = useQuery({
    queryKey: ['measure-template'],
    queryFn: getMeasureTemplate,
    staleTime: 30_000,
  });
  const tplInfo = tplQ.data ?? tpl;

  const showBalloon = pf?.balloon_png !== false;
  const filled = useMemo(
    () => FIELDS.filter((f) => (header[f.key] ?? '').toString().trim()).length,
    [header]);

  return (
    <>
      <div className="modal-backdrop" onClick={busy ? undefined : onClose} />
      <div className="modal mr-modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <div>
            <div className="modal-title">导出测量报告</div>
            <div className="modal-sub">
              图纸 #{drawingId}{pf?.filename ? ` · ${pf.filename}` : ''}
              {' · '}表单 XK240122062
            </div>
          </div>
          <button className="btn sm" onClick={onClose} disabled={busy} title="关闭">✕</button>
        </div>

        <div className="modal-body">
          {pfQ.isLoading && <div className="hint">正在预检图纸与模板…</div>}
          {pfQ.isError && (
            <div className="mr-err">预检失败：{(pfQ.error as any)?.message}</div>
          )}

        {pf && (
          <div className="mr-body">
            {/* ---------------- 会导出什么 ---------------- */}
            <section className="mr-sec">
              <div className="mr-sec-title">将写入的内容
                {/* v0.54：明细出处如实展示 —— 测量报告编辑页存过草稿 ⇒
                    统计与导出都按草稿算（后端 `preflight(items_override)`）。 */}
                {pf.from_draft && (
                  <span className="mr-badge ok" title="在测量报告编辑页增删改过并保存的明细行">
                    用编辑页草稿 {pf.draft_count} 行
                  </span>
                )}
              </div>
              <div className="mr-stats">
                <Stat label="进明细" value={pf.will_export} unit="条" primary />
                <Stat label="特征总数" value={pf.feature_total} unit="条" />
                <Stat label="模板容量" value={pf.template_capacity} unit="行" />
                <Stat label="自动插行"
                      value={pf.insert_rows ? `+${pf.insert_rows}` : '0'}
                      unit="行" warn={pf.insert_rows > 0} />
                <Stat label="参考尺寸（已纳入）" value={pf.reference_count} unit="条" />
                {(pf.gdt_remark ?? 0) > 0 && (
                  <Stat label="形位公差进备注" value={pf.gdt_remark} unit="条" />
                )}
                <Stat label="无公差（T/U 留空）" value={pf.no_tolerance} unit="条"
                      warn={pf.no_tolerance > 0} />
                {pf.needs_manual > 0 && (
                  <Stat label="需人工处理" value={pf.needs_manual} unit="条" warn />
                )}
                {pf.excluded > 0 && (
                  <Stat label="已停用（排除）" value={pf.excluded} unit="条" />
                )}
                {pf.unhide_rows > 0 && (
                  <Stat label="取消隐藏" value={pf.unhide_rows} unit="行" />
                )}
              </div>
              <div className="mr-note">
                A 气泡号 · B 基本尺寸（直径自动带 ⌀ 前缀）· F 测量设备 ·
                T/U 上下偏差 · M 备注（形位公差内容 + 参考尺寸标记）；
                C/D/L 由模板自带公式自动算；E 列 CC/SC 本期留空。
                <span className="mr-tip">
                  {' '}附图页：{showBalloon ? '会放入当前画布页的气泡图' : '保持模板原样'}
                </span>
              </div>
              {pf.warnings.length > 0 && (
                <ul className="mr-warns">
                  {pf.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              )}
            </section>

            {/* ---------------- 模板 ---------------- */}
            <section className="mr-sec">
              <div className="mr-sec-title">
                测量报告模板
                <span className={`mr-badge ${tplInfo?.ok ? 'ok' : 'bad'}`}>
                  {tplInfo?.ok ? '可用' : '不可用'}
                </span>
                <span className="mr-badge">
                  {tplInfo?.source === 'uploaded' ? '自上传' : '内置'}
                </span>
              </div>
              <div className="mr-tplrow">
                <span className="mr-tplname" title={tplInfo?.path}>
                  {tplInfo?.filename || '（未找到模板）'}
                </span>
                <span className="mr-size">
                  {tplInfo?.size_bytes
                    ? `${(tplInfo.size_bytes / 1024).toFixed(0)} KB` : ''}
                </span>
                <input ref={tplRef} type="file" accept=".xlsx" hidden
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         if (f) tplMut.mutate(f);
                         if (tplRef.current) tplRef.current.value = '';
                       }} />
                <button className="btn sm" disabled={tplMut.isPending}
                        title="上传一份新的《测量报告》xlsx 作为模板（不会覆盖旧文件，可随时恢复内置）"
                        onClick={() => tplRef.current?.click()}>
                  {tplMut.isPending ? '上传中…' : '导入模板'}
                </button>
                {tplInfo?.source === 'uploaded' && (
                  <button className="btn sm" disabled={resetMut.isPending}
                          onClick={() => resetMut.mutate()}>恢复内置</button>
                )}
              </div>
              {(tplInfo?.problems?.length ?? 0) > 0 && (
                <ul className="mr-warns">
                  {tplInfo!.problems.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              )}
              <div className="mr-note">
                模板需含「测量报告」与「附图」两个 sheet；替换后立即生效，
                旧模板文件保留在 <code>data/templates/measure_report/</code>。
              </div>
            </section>

            {/* ---------------- 表头 ---------------- */}
            <section className="mr-sec">
              <div className="mr-sec-title">
                表头（3–6 行）
                <span className="mr-badge">
                  已填 {filled}/{FIELDS.length}
                </span>
                <span className="mr-hint">
                  「自动」= 本图标题栏锚定 ·「文件名」= 取自上图纸文件名 ·
                  「上次」= **不是本图的值**，请照图核对或清空
                </span>
              </div>
              <div className="mr-grid">
                {FIELDS.map((f) => {
                  const src = srcOf(f.key);
                  const hs = (pf.header_source as any)?.[f.key];
                  // 只有标题栏锚定才配叫「自动」；文件名是**提示**不是识别结果，
                  // 记忆值更是上一张图的东西 —— 三者必须长得不一样。
                  const isAuto = typeof hs === 'string'
                    && hs.startsWith('auto');
                  const isFile = typeof hs === 'string'
                    && hs.startsWith('filename');
                  // v0.38：图纸已归户时，身份字段取自「零件主数据」台账
                  // （人工确认过的锁定值）—— 它比"本图重抽"更权威，也必须长得不一样。
                  const isLedger = typeof hs === 'string'
                    && hs.startsWith('ledger');
                  const isLast = !isAuto && !isFile && !isLedger
                    && memoFilled.has(f.key as string);
                  return (
                    <label className={`mr-fld ${f.width === 'sm' ? 'sm' : ''}`}
                           key={f.key}>
                      <span className="mr-lab">
                        {f.label}
                        <em className="mr-cell">{f.cell}</em>
                        {isAuto && <em className="mr-auto">自动</em>}
                        {isFile && <em className="mr-file">文件名</em>}
                        {isLedger && <em className="mr-ledger">台账</em>}
                        {isLast && <em className="mr-last">上次</em>}
                      </span>
                      <input
                        className={`mr-inp${src === 'empty' ? ' blank' : ''}`}
                        value={(header[f.key] ?? '') as string}
                        placeholder={f.placeholder || '（抽不到就留空，不编造）'}
                        onChange={(e) => setField(f.key, e.target.value)}
                      />
                    </label>
                  );
                })}
              </div>
              {/* 文件名里有多个图号候选（如「XK24-141 D21064101_5ED_001…」同时含
                  内部图号与客户图号）⇒ 系统**不猜**，摆出来让人点一个。 */}
              {fileCands.length > 1 && !(header.part_no ?? '').toString().trim() && (
                <div className="mr-note">
                  文件名里有 {fileCands.length} 个图号候选，点一个填入「零件编号」
                  （并同步填「图纸编号」）—— 系统不替你判断哪个是零件号：
                  <span className="mr-cands">
                    {fileCands.map((c) => (
                      <button className="btn sm" key={c} type="button"
                              onClick={() => {
                                setField('part_no', c);
                                setField('drawing_no', c);
                              }}>{c}</button>
                    ))}
                  </span>
                </div>
              )}
              {pf.header_note && (
                <div className="mr-note warn">
                  表头自动抽取：{pf.header_note}
                </div>
              )}
              <label className="mr-remember">
                <input type="checkbox" checked={remember}
                       onChange={(e) => setRemember(e.target.checked)} />
                记住这次填写（只记检测人 / 检查日期 / 批号 / 数量这类**跨图复用**的字段，
                并在下次标「上次」；零件编号等本图身份字段从不记忆）
              </label>
            </section>
          </div>
          )}
        </div>

        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>取消</button>
          <button className="btn primary"
                  disabled={busy || !pf || !tplInfo?.ok}
                  title={!tplInfo?.ok ? '模板不可用，请先导入合法模板' : ''}
                  onClick={() => exportMut.mutate()}>
            {busy ? '生成中（含附图，稍候）…' : '导出测量报告'}
          </button>
        </div>
      </div>
    </>
  );
}

function Stat({
  label, value, unit, primary, warn,
}: {
  label: string; value: number | string; unit?: string;
  primary?: boolean; warn?: boolean;
}) {
  return (
    <div className={`mr-stat${primary ? ' primary' : ''}${warn ? ' warn' : ''}`}>
      <div className="mr-stat-v">{value}<em>{unit}</em></div>
      <div className="mr-stat-l">{label}</div>
    </div>
  );
}
